import { getBudgetAmountInRange, hasBudgetAmountChangeBy } from '../../utils/budgetAmounts';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useData } from '../../contexts/DataContext';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  ButtonBase,
  TableFooter,
  styled,
  Card,
  CardContent,
  Grid,
  Divider,
  Tab,
  Tabs,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  LinearProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  TrendingUp,
  TrendingDown,
  PieChart,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  InfoOutlined,
  ShowChart,
  ArrowForward,
  FilterList,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  CloudDownloadOutlined,
  Category,
  QueryStats,
  AccountBalanceWallet,
  Analytics,
  ChevronRight
} from '@mui/icons-material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import CategoryTransactionsDialog from '../Transactions/CategoryTransactionsDialog';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceDetailDialog from '../Balance/AccountBalanceDetailDialog';
import { 
  isCurrentMonthString, 
  isDateInCurrentMonth, 
  formatMonthName, 
  getMonthName, 
  isForecastMonth 
} from '../../utils/dateUtils';
import {
  formatVariance,
  isPositiveVariance,
  formatCurrency,
  formatPercentage,
  calculateTrend,
  getVarianceChipProps,
  getMomChipProps,
  getMetricTrend
} from '../../utils/calculationUtils';
import AccountBalanceDetailDialogWrapper from './AccountBalanceDetailDialogWrapper';
import CategoryTransactionsDialogWrapper from './CategoryTransactionsDialogWrapper';
import { findAccountById, getBudgetTarget, getCategoryColor } from '../../utils/categoryAccountUtils';
import SummaryCard from './SummaryCard';
import NetWorthSection from './NetWorthSection';
import BudgetTableView from './BudgetTableView';
import useBudgetData from './useBudgetData';
import useForecastData from './useForecastData';
import {
  baseMonthOf,
  compareMonthKeys,
  computeProjectedAccountDeltas,
  computeProjectedMonthAmounts,
  isProjectedMonth,
  monthInYear,
} from '../../utils/projectedMonth';
import { computeForecastMovements } from '../../utils/analysisForecast';
import SankeyChart from './SankeyChart';
import MoneyFlowSection from './MoneyFlowSection';
import BudgetProgressCard from './BudgetProgressCard';
import IncomeExpenseChart from './IncomeExpenseChart';
import { prepareSankeyData, prepareChartData } from './SankeyUtils.js';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import InsightsIcon from '@mui/icons-material/Insights';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse, isValid } from 'date-fns';
import { getCurrentNZMonthString, isCurrentNZMonth, getCurrentNZDate } from '../../utils/nzDateUtils';
import PageHeader from '../common/PageHeader';
import MetricCard from '../common/MetricCard';

// Styled components for better visuals
const HeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  color: theme.palette.text.secondary,
  fontWeight: 600,
  padding: theme.spacing(0.75, 1.5),
  fontSize: theme.typography.body3.fontSize,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 0.75),
    fontSize: '0.75rem',
  },
}));

const CategoryHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
  padding: theme.spacing(0.75, 1.5),
  fontSize: theme.typography.body3.fontSize,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 0.75),
    fontSize: '0.75rem',
  },
}));

const TotalRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  fontWeight: 'bold',
  '& .MuiTableCell-root': {
    fontWeight: 600,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    fontSize: theme.typography.body3.fontSize,
    padding: theme.spacing(0.75, 1.5),
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
      padding: theme.spacing(0.5, 0.625),
    },
  },
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  }
}));

const MonthYearToggle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
}));

// Reuse Calendar-style toggles for consistency, but keep them visually inline with the surrounding Paper
const ToggleGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: theme.spacing(2),
  alignItems: 'center',
  justifyContent: 'center',
  // Make transparent / borderless so no inner box is shown
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none',
  padding: 0,
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
    overflowX: 'auto',
  }
}));

const ToggleItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
  minWidth: 80,
  padding: theme.spacing(0.25, 0.6),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'transparent',
  transition: 'all 0.12s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.action.hover, 0.03),
  },
  '& .toggle-label': {
    fontWeight: 600,
    marginBottom: theme.spacing(0.25),
    fontSize: theme.typography.body3.fontSize,
    textAlign: 'center',
  },
  '& + &': {
    marginLeft: theme.spacing(1.5),
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 'auto',
    padding: theme.spacing(0.12, 0.4),
    '& .toggle-label': {
      fontSize: '0.75rem',
      textAlign: 'center',
    },
  },
}));

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName="Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 34,
  height: 20,
  padding: 0,
  display: 'inline-flex',
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '200ms',
    '&.Mui-checked': {
      transform: 'translateX(14px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
        border: 0
      },
      '& .MuiSwitch-thumb': {
        boxShadow: '0 1px 3px rgba(0,0,0,0.45)'
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5
      }
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      boxShadow: '0 0 0 3px rgba(0,0,0,0.25)'
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[400]
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.4
    }
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 16,
    height: 18,
    backgroundColor: '#fff',
    borderRadius: 10,
    boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
  },
  '& .MuiSwitch-track': {
    borderRadius: 20 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E2E5E9' : theme.palette.grey[700],
    border: '1px solid #C5C9D1',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], { duration: 200 })
  }
}));

const PillTabs = styled(Tabs)(({ theme }) => ({
  background: 'rgba(255,255,255,0.12)',
  borderRadius: 32,
  minHeight: 40,
  '& .MuiTabs-indicator': {
    display: 'none',
  },
}));

const PillTab = styled(Tab)(({ theme }) => ({
  borderRadius: 32,
  minHeight: 36,
  minWidth: 120,
  fontWeight: 700,
  margin: theme.spacing(0, 0.5),
  background: 'transparent',
  color: '#fff',
  '&.Mui-selected': {
    background: '#fff',
    color: theme.palette.primary.main,
    boxShadow: theme.shadows[1],
  },
}));

// viewType comes from the route: /cashflow renders 'monthly', /networth 'yearly'.
const BudgetView = ({ viewType = 'monthly' }) => {
  const theme = useTheme();
  const { transactions, categories, budgets, accounts, actions } = useData();
  
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [displayOptions, setDisplayOptions] = useState(['vs-budget', 'vs-prior-month']); // Array of selected options: 'percentages', 'vs-budget', 'vs-prior-month'
  const [anchorEl, setAnchorEl] = useState(null);
  const [showForecast, setShowForecast] = useState(true);
  // Balance is always visible now (removed eye toggle)
  const [accountBalances, setAccountBalances] = useState({});
  const [monthlyForecastBalances, setMonthlyForecastBalances] = useState({});  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const prevMonthlyBalancesRef = useRef();  

  const budgetData = useBudgetData(transactions, categories);
  const [forecastMonths, forecastData] = useForecastData(budgetData, {
    showForecast,
    budgets,
    categories,
    transactions,
    actions
  });

  // Get all available years from the data
  const availableYears = useMemo(() => {
    const years = new Set();
    [...budgetData.months, ...forecastMonths].forEach(monthStr => {
      const year = 2000 + parseInt(monthStr.split('-')[1], 10);
      years.add(year);
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [budgetData.months, forecastMonths]);

  // State for the selected year in yearly view
  const [selectedYear, setSelectedYear] = useState(() => {
    const currentYear = new Date().getFullYear();
    if (availableYears.includes(currentYear)) {
      return currentYear;
    }
    return availableYears.length > 0 ? availableYears[availableYears.length - 1] : currentYear;
  });

  // Update selected year if available years change and current selection is not valid
  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[availableYears.length - 1]);
    }
  }, [availableYears, selectedYear]);

  // Check if a month is a forecast month
  const isForecastMonth = (month) => {
    return forecastMonths.includes(month);
  };

  // Helper function to check if a month is the current month (using NZ timezone)
  const isCurrentMonthString = (monthStr) => {
    return isCurrentNZMonth(monthStr);
  };

  // Helper function to check if a date is in the current month (using NZ timezone)
  const isDateInCurrentMonth = (dateStr) => {
    const date = new Date(dateStr);
    const currentDate = getCurrentNZDate();
    
    return date.getMonth() === currentDate.getMonth() && 
          date.getFullYear() === currentDate.getFullYear();
  };

  // Date range string replicating Dashboard behavior (short month names)
  const getAnalysisDateRangeString = useCallback(() => {
    const formatRange = (startDate, endDate) => {
      const opts = { year: 'numeric', month: 'short', day: 'numeric' };
      return `${startDate.toLocaleDateString('en-US', opts)} - ${endDate.toLocaleDateString('en-US', opts)}`;
    };

    if (viewType === 'monthly' && selectedMonth) {
      const [monthAbbr, yearStr] = selectedMonth.split('-');
      const monthIndex = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].indexOf(monthAbbr);
      const year = 2000 + parseInt(yearStr, 10);
      if (monthIndex >= 0) {
        const startDate = new Date(year, monthIndex, 1);
        const endDate = new Date(year, monthIndex + 1, 0);
        return formatRange(startDate, endDate);
      }
    }
    if (viewType === 'yearly') {
      const year = selectedYear || new Date().getFullYear();
      return formatRange(new Date(year, 0, 1), new Date(year, 11, 31));
    }
    // Fallback current month
    const now = new Date();
    return formatRange(new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0));
  }, [viewType, selectedMonth, selectedYear]);

  // Helper to count weekly occurrences in a month
  const countWeeklyOccurrencesInMonth = (startDate, year, month) => {
    // Get the day of week from start date (0-6)
    const startDay = new Date(startDate).getDay();
    
    // Create date for first day of target month
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    // Find first occurrence of this weekday
    let date = new Date(firstDayOfMonth);
    while (date.getDay() !== startDay) {
      date.setDate(date.getDate() + 1);
    }
    
    // Count occurrences
    let count = 0;
    while (date <= lastDayOfMonth) {
      count++;
      date.setDate(date.getDate() + 7);
    }
    
    return count;
  };

  // Create a dateRange based on selectedMonth
  const getDateRangeForSelectedMonth = useMemo(() => {
    if (!selectedMonth) return null;
    
    const [monthAbbr, yearStr] = selectedMonth.split('-');
    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthAbbr);
    const year = 2000 + parseInt(yearStr, 10);
    
    const startDate = new Date(year, monthIndex, 1);
    const endDate = new Date(year, monthIndex + 1, 0); // Last day of month
    
    return { startDate, endDate };
  }, [selectedMonth]);

  // Budget data is keyed by category ID; resolve to display names for UI text
  const categoryDisplayNameMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { if (c.id) map[c.id] = c.name || c.id; });
    return map;
  }, [categories]);
  const getCategoryDisplayName = (categoryId) =>
    categoryId === 'uncategorized' ? 'Uncategorized' : (categoryDisplayNameMap[categoryId] || categoryId);

  // Update handleCategoryClick to use the wrapper's openDialog
  const handleCategoryClick = (categoryId, specificMonthStr = null, reportType = null) => {
    let dateRange = null;
    let title = '';
    if (specificMonthStr) {
      const [monthAbbr, yearStr] = specificMonthStr.split('-');
      const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthAbbr);
      const year = 2000 + parseInt(yearStr, 10);
      const startDate = new Date(year, monthIndex, 1);
      const endDate = new Date(year, monthIndex + 1, 0);
      dateRange = { startDate, endDate };
      title = `Transactions for ${getCategoryDisplayName(categoryId)} in ${formatMonthName(specificMonthStr)}`;
    } else if (categoryId) {
      dateRange = null;
      title = `Transactions for ${getCategoryDisplayName(categoryId)} in ${selectedMonth ? formatMonthName(selectedMonth) : ''}`;
    }
    if (categoryDialogRef.current && categoryDialogRef.current.openDialog) {
      categoryDialogRef.current.openDialog(categoryId, dateRange, title, reportType);
    }
  };

  // Update the handleAccountBalanceClick function to use the new calculation function
  const handleAccountBalanceClick = (accountId, month) => {
    if (accountBalanceDialogRef.current && accountBalanceDialogRef.current.openDialog) {
      accountBalanceDialogRef.current.openDialog(accountId, month);
    }
  };

  // Calculate current month for default selection
  useEffect(() => {
    if ((budgetData.months.length > 0 || forecastMonths.length > 0) && !selectedMonth) {
      // Always try to set current NZ month as selected (it should now be in budgetData.months)
      const currentNZMonth = getCurrentNZMonthString();
      const hasCurrentMonthInData = budgetData.months.includes(currentNZMonth);
      
      if (hasCurrentMonthInData) {
        // Current month is in actual data (preferred)
        setSelectedMonth(currentNZMonth);
      } else {
        // Fallback to the most recent month available (shouldn't happen now)
        setSelectedMonth(budgetData.months[budgetData.months.length - 1]);
      }
    }
  }, [budgetData.months, selectedMonth, forecastMonths]);
  
  // Calculate budget totals
  const calculateBudgetTotal = () => {
    const total = {};
    budgetData.months.forEach(month => {
      total[month] = budgetData.monthlySummary[month].income - budgetData.monthlySummary[month].expenses;
    });
    
    // Add forecast totals if showing forecasts
    if (showForecast) {
      forecastMonths.forEach(month => {
        total[month] = forecastData.monthlySummary[month].income - forecastData.monthlySummary[month].expenses;
      });
    }
    
    return total;
  };

  // Format percentage
  const formatPercentage = (value, total) => {
    if (total === 0) return '0%';
    return `${Math.round((value / total) * 100)}%`;
  };

  // Get budget target for a category
  const getBudgetTarget = (categoryId, isIncome, monthStr) => {
    // Find budgets for this category
    const categoryBudgets = budgets.filter(budget => 
      budget.categoryId === categoryId && 
      budget.type === (isIncome ? 'Income' : 'Expense') && 
      budget.isActive
    );
    
    if (categoryBudgets.length > 0) {
      // Use the provided month string or default to selected month
      const currentMonthStr = monthStr || selectedMonth;
      if (!currentMonthStr) return 0;
      
      // Parse month string (e.g., "Jan-25") to date range. The projected
      // column budgets against its own calendar month.
      const [monthAbbr, yearStr] = baseMonthOf(currentMonthStr).split('-');
      const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthAbbr);
      const year = 2000 + parseInt(yearStr, 10);
      
      const startOfMonth = new Date(year, monthIndex, 1);
      const endOfMonth = new Date(year, monthIndex + 1, 0); // Last day of month
      
      // Sum up budgets that apply to this month
      let totalBudget = 0;
      
      categoryBudgets.forEach(budget => {
        if (hasBudgetAmountChangeBy(budget, endOfMonth)) { totalBudget += getBudgetAmountInRange(budget, startOfMonth, endOfMonth); return; }
        const budgetStart = new Date(budget.startDate);
        const budgetEnd = budget.endDate ? new Date(budget.endDate) : new Date(2099, 11, 31); // Far future if no end date
        
        // Check if budget applies to this month
        if (budgetStart <= endOfMonth && budgetEnd >= startOfMonth) {
          // If recurring, calculate prorated amount
          if (budget.isRecurring) {
            // Different calculation based on recurring period
            switch (budget.recurringPeriod) {
              case 'monthly':
                totalBudget += budget.isProrated ? (() => {
                  const overlapStart = new Date(Math.max(budgetStart, startOfMonth));
                  const overlapEnd = new Date(Math.min(budgetEnd, endOfMonth));
                  const daysOverlap = (overlapEnd - overlapStart) / (1000 * 60 * 60 * 24) + 1;
                  const daysInMonthLocal = (endOfMonth - startOfMonth) / (1000 * 60 * 60 * 24) + 1;
                  return budget.amount * (daysOverlap / daysInMonthLocal);
                })() : budget.amount;
                break;
              case 'quarterly':
                totalBudget += budget.isProrated ? (() => {
                  const overlapStart = new Date(Math.max(budgetStart, startOfMonth));
                  const overlapEnd = new Date(Math.min(budgetEnd, endOfMonth));
                  const daysOverlap = (overlapEnd - overlapStart) / (1000 * 60 * 60 * 24) + 1;
                  return budget.amount * (daysOverlap / 91.25);
                })() : (() => {
                  const startMonth = budgetStart.getMonth();
                  const diff = (startOfMonth.getFullYear() - budgetStart.getFullYear()) * 12 + (startOfMonth.getMonth() - startMonth);
                  return (diff >= 0 && diff % 3 === 0) ? budget.amount : 0;
                })();
                break;
              case 'yearly':
                totalBudget += budget.isProrated ? (() => {
                  const overlapStart = new Date(Math.max(budgetStart, startOfMonth));
                  const overlapEnd = new Date(Math.min(budgetEnd, endOfMonth));
                  const daysOverlap = (overlapEnd - overlapStart) / (1000 * 60 * 60 * 24) + 1;
                  return budget.amount * (daysOverlap / 365);
                })() : (budget.amount / 12);
                break;
              case 'weekly':
                if (budget.isProrated) {
                  const overlapStart = new Date(Math.max(budgetStart, startOfMonth));
                  const overlapEnd = new Date(Math.min(budgetEnd, endOfMonth));
                  const daysOverlap = (overlapEnd - overlapStart) / (1000 * 60 * 60 * 24) + 1;
                  totalBudget += (budget.amount / 7) * daysOverlap;
                } else {
                  // Count actual weekday occurrences
                  const targetDow = budgetStart.getDay();
                  let count = 0;
                  for (let d = new Date(startOfMonth); d <= endOfMonth; d.setDate(d.getDate() + 1)) {
                    if (d >= budgetStart && d <= budgetEnd && d.getDay() === targetDow) count++;
                  }
                  totalBudget += count * budget.amount;
                }
                break;
              default:
                totalBudget += budget.amount;
            }
          } else {
            // Non-recurring budget - should be prorated if it doesn't cover the full month
            const daysInMonth = (endOfMonth - startOfMonth) / (1000 * 60 * 60 * 24) + 1;
            const overlapStart = new Date(Math.max(budgetStart, startOfMonth));
            const overlapEnd = new Date(Math.min(budgetEnd, endOfMonth));
            const daysOverlap = (overlapEnd - overlapStart) / (1000 * 60 * 60 * 24) + 1;
            
            // Ensure budget amount is positive for calculation
            totalBudget += Math.abs(budget.amount) * (daysOverlap / daysInMonth);
          }
        }
      });
      
      return totalBudget;
    }
    
    return 0;
  };

  // Sort categories by amount for the selected month
  const sortedCategories = useMemo(() => {
    if (!selectedMonth) return { income: [], expenses: [] };

    const sortIncomeCategories = Object.keys(budgetData.incomeCategories)
      .map(category => ({
        name: category,
        amount: budgetData.incomeCategories[category][selectedMonth] || 0
      }))
      .sort((a, b) => b.amount - a.amount);

    const sortExpenseCategories = Object.keys(budgetData.expenseCategories)
      .map(category => ({
        name: category,
        amount: budgetData.expenseCategories[category][selectedMonth] || 0
      }))
      .sort((a, b) => b.amount - a.amount);

    return { income: sortIncomeCategories, expenses: sortExpenseCategories };
  }, [budgetData, selectedMonth]);

  // Calculate summary for selected month
  const selectedMonthSummary = useMemo(() => {
    if (!selectedMonth) {
      return { income: 0, expenses: 0, balance: 0 };
    }
    
    // Check if it's a forecast month
    const isForecast = forecastMonths.includes(selectedMonth);
    
    let income = 0;
    let expenses = 0;
    
    if (isForecast) {
      income = forecastData.monthlySummary[selectedMonth]?.income || 0;
      expenses = forecastData.monthlySummary[selectedMonth]?.expenses || 0;
    } else {
      income = budgetData.monthlySummary[selectedMonth]?.income || 0;
      expenses = budgetData.monthlySummary[selectedMonth]?.expenses || 0;
    }
    
    const balance = income - expenses;
    
    return { income, expenses, balance, isForecast };
  }, [budgetData, selectedMonth, forecastData, forecastMonths, showForecast]);

  // Find previous and next months for navigation
  const adjacentMonths = useMemo(() => {
    if (!selectedMonth) {
      return { prev: null, next: null };
    }

    // Combine actual and forecast months for navigation
    const allMonths = [...budgetData.months];
    if (showForecast) {
      allMonths.push(...forecastMonths);
    }
    
    const sortedMonths = allMonths.sort((a, b) => {
      const [aMonth, aYear] = a.split('-');
      const [bMonth, bYear] = b.split('-');
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      if (aYear !== bYear) return aYear - bYear;
      return monthOrder.indexOf(aMonth) - monthOrder.indexOf(bMonth);
    });
    
    const currentIndex = sortedMonths.indexOf(selectedMonth);
    const prev = currentIndex > 0 ? sortedMonths[currentIndex - 1] : null;
    const next = currentIndex < sortedMonths.length - 1 ? sortedMonths[currentIndex + 1] : null;

    return { prev, next };
  }, [budgetData.months, selectedMonth, forecastMonths, showForecast]);

  // Format month name for display
  const formatMonthName = (monthStr) => {
    if (!monthStr) return '';
    
    const [month, year] = monthStr.split('-');
    const monthNames = {
      'Jan': 'January', 'Feb': 'February', 'Mar': 'March', 
      'Apr': 'April', 'May': 'May', 'Jun': 'June',
      'Jul': 'July', 'Aug': 'August', 'Sep': 'September', 
      'Oct': 'October', 'Nov': 'November', 'Dec': 'December'
    };
    
    // Check if it's a forecast month and add indicator
    const isForecast = forecastMonths.includes(monthStr);
    const monthName = `${monthNames[month]} 20${year}`;
    
    return isForecast ? `${monthName} (Forecast)` : monthName;
  };

  // Handle month navigation
  const handlePrevMonth = () => {
    if (adjacentMonths.prev) {
      setSelectedMonth(adjacentMonths.prev);
    }
  };

  const handleNextMonth = () => {
    if (adjacentMonths.next) {
      setSelectedMonth(adjacentMonths.next);
    }
  };

  // Handle year navigation
  const handlePrevYear = () => {
    const currentIndex = availableYears.indexOf(selectedYear);
    if (currentIndex > 0) {
      setSelectedYear(availableYears[currentIndex - 1]);
    }
  };

  const handleNextYear = () => {
    const currentIndex = availableYears.indexOf(selectedYear);
    if (currentIndex < availableYears.length - 1) {
      setSelectedYear(availableYears[currentIndex + 1]);
    }
  };

  // Handle forecast toggle
  const handleForecastToggle = (event) => {
    setShowForecast(event.target.checked);
    
    // If turning on forecast and we're at the last actual month, 
    // navigate to the first forecast month
    if (event.target.checked && forecastMonths.length > 0) {
      const isLastActualMonth = selectedMonth === budgetData.months[budgetData.months.length - 1];
      if (isLastActualMonth) {
        setSelectedMonth(forecastMonths[0]);
      }
    }
    
    // If turning off forecast and we're in a forecast month, 
    // navigate back to the last actual month
    if (!event.target.checked && forecastMonths.includes(selectedMonth)) {
      setSelectedMonth(budgetData.months[budgetData.months.length - 1]);
    }
  };  

  // Calculate trends (% change from previous month)
  const calculateTrend = (month) => {
    if (!month) return { income: 0, expenses: 0, balance: 0 };
    
    const monthIndex = budgetData.months.indexOf(month);
    if (monthIndex <= 0) return { income: 0, expenses: 0, balance: 0 };
    
    const prevMonth = budgetData.months[monthIndex - 1];
    
    const currentIncome = budgetData.monthlySummary[month].income;
    const prevIncome = budgetData.monthlySummary[prevMonth].income;
    const incomeTrend = prevIncome === 0 ? (currentIncome > 0 ? 100 : 0) : ((currentIncome - prevIncome) / prevIncome) * 100;
    
    const currentExpenses = budgetData.monthlySummary[month].expenses;
    const prevExpenses = budgetData.monthlySummary[prevMonth].expenses;
    const expensesTrend = prevExpenses === 0 ? (currentExpenses > 0 ? 100 : 0) : ((currentExpenses - prevExpenses) / prevExpenses) * 100;
    
    const currentBalance = currentIncome - currentExpenses;
    const prevBalance = prevIncome - prevExpenses;
    // Special handling for balance trend when previous balance is near zero
    let balanceTrend;
    if (Math.abs(prevBalance) < 0.01) { // Use a small threshold for near-zero
       balanceTrend = currentBalance > 0 ? 100 : (currentBalance < 0 ? -100 : 0);
    } else {
      balanceTrend = ((currentBalance - prevBalance) / Math.abs(prevBalance)) * 100;
    }
    
    return { 
      income: Math.round(incomeTrend), 
      expenses: Math.round(expensesTrend), 
      balance: Math.round(balanceTrend) 
    };
  };

  const selectedMonthTrend = useMemo(() => {
    return calculateTrend(selectedMonth);
  }, [selectedMonth, budgetData]);

  // Period-over-period trends for the summary cards: the selected month is
  // always compared against the immediately preceding month.
  const selectedMonthCardTrends = useMemo(() => {
    const summaryFor = (month) => {
      if (!month) return null;
      const src = forecastMonths.includes(month) ? forecastData.monthlySummary : budgetData.monthlySummary;
      const s = src[month];
      if (!s) return null;
      return { income: s.income, expenses: s.expenses, balance: s.income - s.expenses };
    };

    const prevMonth = adjacentMonths.prev;
    const current = summaryFor(selectedMonth);
    const previous = summaryFor(prevMonth);
    if (!current || !previous) return {};

    const label = `vs ${prevMonth}`;
    return {
      income: getMetricTrend(current.income, previous.income, true, label),
      expenses: getMetricTrend(current.expenses, previous.expenses, false, label),
      balance: getMetricTrend(current.balance, previous.balance, true, label),
    };
  }, [selectedMonth, adjacentMonths, budgetData, forecastData, forecastMonths]);

  // Generate unique category colors
  const getCategoryColor = (index, type) => {
    const baseColors = type === 'income' 
      ? [theme.palette.success.light, theme.palette.success.main, theme.palette.success.dark]
      : [theme.palette.error.light, theme.palette.error.main, theme.palette.error.dark];
    
    // Create variations based on index
    const colorIndex = index % baseColors.length;
    const shade = Math.floor(index / baseColors.length);
    
    // Adjust color based on shade
    const color = baseColors[colorIndex];
    
    // Convert to rgba and adjust alpha
    const alpha = 1 - (shade * 0.15);
    return color; // Returning the base color for now, can adjust alpha later if needed
  };

  // Sort categories alphabetically for display
  const sortedIncomeCategories = useMemo(() => {
    const categoryIds = Object.keys(budgetData.incomeCategories);
    
    // Create a map of category ID to order
    const orderMap = {};
    categories.forEach(cat => {
      orderMap[cat.id] = cat.order !== undefined ? cat.order : 999999;
    });
    
    // Sort by order, with fallback to name sort
    return categoryIds.sort((a, b) => {
      // Handle 'uncategorized' specifically if needed, e.g., place it last
      if (a === 'uncategorized') return 1;
      if (b === 'uncategorized') return -1;

      // Sort by order if available
      const orderA = orderMap[a] !== undefined ? orderMap[a] : 999999;
      const orderB = orderMap[b] !== undefined ? orderMap[b] : 999999;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // Fall back to name sort
      return a.localeCompare(b);
    });
  }, [budgetData.incomeCategories, categories]);
  
  const sortedExpenseCategories = useMemo(() => {
    const categoryIds = Object.keys(budgetData.expenseCategories);
    
    // Create a map of category ID to order
    const orderMap = {};
    categories.forEach(cat => {
      orderMap[cat.id] = cat.order !== undefined ? cat.order : 999999;
    });
    
    // Sort by order, with fallback to name sort
    return categoryIds.sort((a, b) => {
      // Handle 'uncategorized' specifically if needed, e.g., place it last
      if (a === 'uncategorized') return 1;
      if (b === 'uncategorized') return -1;
      
      // Sort by order if available
      const orderA = orderMap[a] !== undefined ? orderMap[a] : 999999;
      const orderB = orderMap[b] !== undefined ? orderMap[b] : 999999;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // Fall back to name sort
      return a.localeCompare(b);
    });
  }, [budgetData.expenseCategories, categories]);

  // Add Sankey chart data preparation
  const sankeyData = useMemo(() => {
    return prepareSankeyData(selectedMonth, budgetData, accounts, categories);
  }, [selectedMonth, budgetData, accounts, categories]);

  // Add Income vs Expenses chart data preparation
  const incomeExpenseChartData = useMemo(() => {
    return prepareChartData(budgetData, budgetData.months.length);
  }, [budgetData]);

  // When selectedMonth changes, update the trend chart to show the last 12 months
  const incomeExpenseChartDataForMonthView = useMemo(() => {
    if (!selectedMonth) return [];

    const allSortedMonths = [...budgetData.months, ...forecastMonths].sort((a, b) => {
      const [aMonth, aYear] = a.split('-');
      const [bMonth, bYear] = b.split('-');
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      if (aYear !== bYear) return parseInt(aYear) - parseInt(bYear);
      return monthOrder.indexOf(aMonth) - monthOrder.indexOf(bMonth);
    });

    const currentMonthIndex = allSortedMonths.indexOf(selectedMonth);
    if (currentMonthIndex < 0) return [];

    // Create a new budgetData object with months filtered up to the selected month
    const monthsForChart = allSortedMonths.slice(0, currentMonthIndex + 1);
    const filteredBudgetData = {
      ...budgetData,
      months: monthsForChart,
    };

    // Ask prepareChartData to process the last 12 months of the provided data
    return prepareChartData(filteredBudgetData, 12);
  }, [selectedMonth, budgetData, forecastMonths]);

  const displayMonths = useMemo(() => {
    // Combine actual and forecast months if forecast is enabled
    const months = showForecast 
      ? [...budgetData.months, ...forecastMonths]
      : budgetData.months;
    
    // Sort combined months (projected columns sort just after their own month)
    const sortedMonths = [...months].sort(compareMonthKeys);

    if (viewType === 'last12Months') {
      return sortedMonths.slice(-12); // Last 12 months
    }

    if (viewType === 'yearly') {
      const yearShort = selectedYear.toString().slice(-2);
      return sortedMonths.filter(m => monthInYear(m, yearShort));
    }
  
    return sortedMonths; // default to all for now
  }, [viewType, budgetData.months, forecastMonths, showForecast, selectedYear]);

  const calculateBasicAccountBalances = useCallback((monthStr) => {
    if (!monthStr) return {};
  
    const [monthAbbr, yearStr] = monthStr.split('-');
    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthAbbr);
    const year = 2000 + parseInt(yearStr, 10);
  
    const monthStart = new Date(year, monthIndex, 1);
    const monthEnd = new Date(year, monthIndex + 1, 0); // Last day of month
    const now = getCurrentNZDate();
  
    const isFutureMonth = monthStart > now;
    
    // Create a result object to store account balances for this month
    const monthBalances = {};
    
    // For each account, calculate a BASIC balance for this month
    accounts.forEach(account => {
      if (!account.isActive) return;
      
      let currentBalance = 0;
      
      if (!isFutureMonth) {
        // For past or current months, use the most recent transaction
        const monthTxns = transactions
          .filter(t => (t.account === account.id) && 
                    new Date(t.date) >= monthStart && 
                    new Date(t.date) <= monthEnd)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        
        if (monthTxns.length > 0) {
          currentBalance = monthTxns[0].balance ?? 0;
        } else {
          // No transactions this month: carry forward the balance from the
          // most recent transaction before the month ended, so quiet months
          // show the true end-of-month balance instead of the live one.
          const priorTxns = transactions
            .filter(t => (t.account === account.id) &&
                      new Date(t.date) <= monthEnd)
            .sort((a, b) => new Date(b.date) - new Date(a.date));

          if (priorTxns.length > 0) {
            currentBalance = priorTxns[0].balance ?? 0;
          } else if (transactions.some(t => t.account === account.id)) {
            // History starts after this month — the account had no balance yet
            currentBalance = 0;
          } else {
            // Account has no transactions at all: live balance is all we have
            currentBalance = account.balance ?? 0;
          }
        }
      } else {
        // For future months, just use account balance as placeholder
        currentBalance = account.balance ?? 0;
      }
      
      // Store the calculated balance
      monthBalances[account.id] = { balance: currentBalance };
    });
    
    return monthBalances;
  }, [accounts, transactions]);  

  const allAccountBalancesByMonth = useMemo(() => {
    const balances = {};
    //console.log('🔍 displayMonths:', displayMonths);
  
    // Calculate balances for each month independently
    displayMonths.forEach((month) => {
      // Get the month-specific balances directly from calculateAccountBalances
      const monthBalances = calculateBasicAccountBalances(month);
      //console.log(`→ [${idx}] month="${month}" →`, monthBalances);
      balances[month] = monthBalances;
    });
    //console.log('✅ allAccountBalancesByMonth result:', balances);
    return balances;
  }, [displayMonths, calculateBasicAccountBalances]);

  const calculateAccountBalances = useCallback((monthStr) => {
    if (!monthStr) return {};

    // Use the calculated balances if available, otherwise calculate from scratch
    if (allAccountBalancesByMonth[monthStr]) {
      return allAccountBalancesByMonth[monthStr];
    }    
  
    const [monthAbbr, yearStr] = monthStr.split('-');
    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthAbbr);
    const year = 2000 + parseInt(yearStr, 10);
  
    const monthStart = new Date(year, monthIndex, 1);
    const monthEnd = new Date(year, monthIndex + 1, 0); // Last day of month
    const now = getCurrentNZDate();
  
    const isFutureMonth = monthStart > now;
    
    // Create a result object to store account balances for this month
    const monthBalances = {};
    
    // For each account, calculate balance for this month
    accounts.forEach(account => {
      // Always start with a balance of 0
      let currentBalance = 0;
      
      if (!isFutureMonth) {
        // For past or current months:
        
        // Find transactions specifically for this month and account
        const monthTxns = transactions
          .filter(t => (t.account === account.id) && 
                    new Date(t.date) >= monthStart && 
                    new Date(t.date) <= monthEnd)
          .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date descending
        
        if (monthTxns.length > 0) {
          // If there are transactions this month, use the balance from the last one
          currentBalance = monthTxns[0].balance ?? 0;
        }
        // If no transactions this month, balance remains 0
      } else {
        // For future months:
        // Start with a balance of 0 and apply budgets/actions
        
        // For future months, apply budgets and actions
        const accountBudgets = budgets.filter(b => b.accountId === account.id && b.isActive);
        
        accountBudgets.forEach(budget => {
          const budgetStart = new Date(budget.startDate);
          const budgetEnd = budget.endDate ? new Date(budget.endDate) : new Date(2099, 11, 31);
          
          if (budgetStart <= monthEnd && budgetEnd >= monthStart) {
            let amount = budget.amount;
            
            if (budget.isRecurring) {
              if (budget.isProrated) {
                const overlapStart = new Date(Math.max(budgetStart, monthStart));
                const overlapEnd = new Date(Math.min(budgetEnd, monthEnd));
                const daysOverlap = (overlapEnd - overlapStart) / (1000 * 60 * 60 * 24) + 1;
                switch (budget.recurringPeriod) {
                  case 'weekly':
                    amount = (budget.amount / 7) * daysOverlap; break;
                  case 'monthly': {
                    const daysInMonthLocal = (monthEnd - monthStart) / (1000 * 60 * 60 * 24) + 1;
                    amount = budget.amount * (daysOverlap / daysInMonthLocal); break; }
                  case 'quarterly':
                    amount = budget.amount * (daysOverlap / 91.25); break;
                  case 'yearly':
                    amount = budget.amount * (daysOverlap / 365); break;
                  default:
                    break;
                }
              } else {
                switch (budget.recurringPeriod) {
                  case 'monthly':
                    break;
                  case 'quarterly': {
                    const startMonth = budgetStart.getMonth();
                    const diff = (monthStart.getFullYear() - budgetStart.getFullYear()) * 12 + (monthStart.getMonth() - startMonth);
                    amount = (diff >= 0 && diff % 3 === 0) ? budget.amount : 0; break; }
                  case 'yearly':
                    amount /= 12; break;
                  case 'weekly': {
                    const targetDow = budgetStart.getDay();
                    let count = 0;
                    for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
                      if (d >= budgetStart && d <= budgetEnd && d.getDay() === targetDow) count++;
                    }
                    amount = count * budget.amount; break; }
                  default:
                    break;
                }
              }
            }
            
            if (hasBudgetAmountChangeBy(budget, monthEnd)) amount = getBudgetAmountInRange(budget, monthStart, monthEnd);
            if (budget.type === 'Income') {
              currentBalance += amount;
            } else if (budget.type === 'Expense') {
              currentBalance -= amount;
            }
          }
        });
        
        // Apply actions for this account and month
        const accountActions = actions.filter(action => {
          const actionStart = new Date(action.startDate);
          const actionEnd = action.endDate ? new Date(action.endDate) : null;
          
          if (!action.isActive || action.isCompleted) return false;
          
          if (action.frequency === 'once') {
            return actionStart >= monthStart && actionStart <= monthEnd;
          }
          
          return actionStart <= monthEnd && (!actionEnd || actionEnd >= monthStart);
        });
        
        accountActions.forEach(action => {
          let occurrences = 1;
          
          switch (action.frequency) {
            case 'weekly':
              occurrences = 4;
              break;
            case 'monthly':
              occurrences = 1;
              break;
            case 'yearly':
              occurrences = 1 / 12;
              break;
          }
          
          if (action.type === 'income' && (action.sourceAccountId === account.id || action.destinationAccountId === account.id)) {
            currentBalance += action.amount * occurrences;
          }
          
          if (action.type === 'expense' && action.sourceAccountId === account.id) {
            currentBalance -= action.amount * occurrences;
          }
          
          if (action.type === 'transfer') {
            if (action.sourceAccountId === account.id) {
              currentBalance -= action.amount * occurrences;
            }
            if (action.destinationAccountId === account.id) {
              currentBalance += action.amount * occurrences;
            }
          }
        });
      }
      
      // Store the calculated balance for this account and month
      monthBalances[account.id] = { balance: currentBalance };
    });
    
    return calculateBasicAccountBalances(monthStr);
  }, [allAccountBalancesByMonth, calculateBasicAccountBalances]);

  // Memoize the selected month's account balances to prevent recalculation
  const selectedMonthBalances = useMemo(() => {
    if (!selectedMonth) return {};
    // logDebug(`Calculating memoized balances for month: ${selectedMonth}`);
    return calculateAccountBalances(selectedMonth);
  }, [selectedMonth, calculateAccountBalances]);

  // The projected column's key, and how much each account still moves before
  // the live month closes. Empty unless the projection is switched on.
  const projectedMonthKey = useMemo(
    () => forecastMonths.find(isProjectedMonth) || null,
    [forecastMonths]
  );

  const projectedAccountDeltas = useMemo(() => {
    if (!projectedMonthKey) return {};
    const projectedAmounts = computeProjectedMonthAmounts({
      month: projectedMonthKey,
      categories,
      transactions,
      actions,
      budgetData,
    });
    return computeProjectedAccountDeltas({
      month: projectedMonthKey,
      accounts,
      budgets,
      categories,
      actions,
      transactions,
      projectedAmounts,
      budgetData,
    });
  }, [projectedMonthKey, accounts, budgets, categories, actions, transactions, budgetData]);


  const calculateAccountBalanceDetails = useCallback((accountId, month) => {
    const account = accounts.find(a => a.id === accountId);
    if (!account) return null;
    
    // Parse month string to get date range (the projected column shares its
    // base month's dates)
    const [monthName, yearStr] = baseMonthOf(month).split('-');
    const monthIdx = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthName);
    const year = 2000 + parseInt(yearStr, 10);

    const startDate = new Date(year, monthIdx, 1);
    const endDate = new Date(year, monthIdx + 1, 0); // Last day of month

    // Determine if this is a forecast month - use correct spelling here
    const isForecastMonth = forecastMonths.includes(month);
    const isProjectedColumn = isProjectedMonth(month);
    // console.log(`Calculating balance details for ${account.name} in ${month} (Forecast: ${isForecastMonth})`);
    
    // ------------------------
    // 1. Calculate starting balance
    // ------------------------
    let startingBalance = 0;
    
    // Sort all months chronologically
    const allMonths = [...budgetData.months, ...forecastMonths].sort(compareMonthKeys);
    
    // Find previous month
    const monthPosition = allMonths.indexOf(month);

    if (monthPosition > 0) {
      const prevMonth = allMonths[monthPosition - 1];
      
      // Get previous month's ending balance
      if (forecastMonths.includes(prevMonth)) {
        // Previous month is a forecast month
        // Check for calculated balance in monthlyForecastBalances
        if (monthlyForecastBalances && 
            monthlyForecastBalances[prevMonth] && 
            monthlyForecastBalances[prevMonth][accountId] !== undefined) {
          startingBalance = monthlyForecastBalances[prevMonth][accountId];
          // console.log(`Using previous forecast month (${prevMonth}) balance: ${startingBalance}`);
        } else {
          // If no forecast balance available yet, use last actual month balance
          const lastActualMonth = budgetData.months[budgetData.months.length - 1];
          if (allAccountBalancesByMonth && allAccountBalancesByMonth[lastActualMonth] && 
              allAccountBalancesByMonth[lastActualMonth][accountId]) {
            startingBalance = allAccountBalancesByMonth[lastActualMonth][accountId].balance;
          } else {
            startingBalance = account.balance || 0;
          }
          // console.log(`Using fallback balance (no forecast available): ${startingBalance}`);
        }
      } else {
        // Previous month is an actual month
        if (allAccountBalancesByMonth && 
            allAccountBalancesByMonth[prevMonth] && 
            allAccountBalancesByMonth[prevMonth][accountId]) {
          startingBalance = allAccountBalancesByMonth[prevMonth][accountId].balance;
          // console.log(`Using previous actual month (${prevMonth}) balance: ${startingBalance}`);
        } else {
          // Fallback to account balance if no calculated balance is available
          startingBalance = account.balance || 0;
          // console.log(`Using account balance (no previous month data): ${startingBalance}`);
        }
      }
    } else {
      // If it's the first month, use the most recent transaction balance
      const accountTransactions = transactions
        .filter(t => t.account === accountId)
        .sort((a, b) => new Date(b.date) - new Date(a.date));
        
      if (accountTransactions.length > 0) {
        startingBalance = accountTransactions[0].balance || 0;
        // console.log(`Using latest transaction balance: ${startingBalance}`);
      } else {
        startingBalance = account.balance || 0;
        // console.log(`Using account's current balance: ${startingBalance}`);
      }
    }
    
    // ------------------------
    // 2. Calculate actual transaction movements (sum of all transactions in this month)
    // ------------------------
    const transactionMovements = [];
    let totalTransactionMovement = 0;
    
    if (!isForecastMonth) {  // Use correct spelling here
      // For actual months, use real transactions
      const monthTransactions = transactions
        .filter(t => 
          t.account === accountId && 
          new Date(t.date) >= startDate &&
          new Date(t.date) <= endDate
        )
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      
      // Group transactions by category
      const categoryTotals = {};
      
      monthTransactions.forEach(transaction => {
        const categoryId = transaction.category || 'uncategorized';
        const category = categories.find(c => c.id === categoryId) || { name: 'Uncategorized', type: 'Mixed' };
        const categoryName = category.name || categoryId;
        const amount = transaction.amount || 0;
        
        if (!categoryTotals[categoryId]) {
          categoryTotals[categoryId] = {
            name: categoryName,
            type: category.type,
            amount: 0,
            transactions: []
          };
        }
        
        categoryTotals[categoryId].amount += amount;
        categoryTotals[categoryId].transactions.push(transaction);
        totalTransactionMovement += amount;
      });
      
      // Create a movement entry for each category
      Object.values(categoryTotals).forEach(category => {
        const isIncome = category.type === 'Income' || 
                        (category.type === 'Mixed' && category.amount > 0);
        
        transactionMovements.push({
          type: isIncome ? 'income' : 'expense',
          category: category.name,
          description: `${isIncome ? 'Income' : 'Expense'} for ${category.name}`,
          amount: category.amount,
          transactionCount: category.transactions.length
        });
      });
    }
    
    // ------------------------
    // 3. Budget movements for forecast months — the shared Analysis forecast
    // math (also drives the Accounts page cards' dashed forecast)
    // ------------------------
    let budgetMovements = [];
    let totalBudgetMovement = 0;
    let forecastMovements = null;

    if (isProjectedColumn) {
      // The projected column opens on the live month's actual-to-date balance
      // (its predecessor in the chain), so its only movement is what is still
      // to come — never a whole month of budgets, which would double count the
      // part of the month already banked.
      const delta = projectedAccountDeltas[accountId] || 0;
      totalBudgetMovement = delta;
      budgetMovements = delta
        ? [{
            type: delta > 0 ? 'income' : 'expense',
            category: 'Rest of month',
            description: `Projected remainder of ${baseMonthOf(month)}`,
            amount: delta,
          }]
        : [];
    } else if (isForecastMonth) {
      forecastMovements = computeForecastMovements({
        accountId,
        month,
        accounts,
        budgets,
        categories,
        actions,
        forecastAmounts: forecastData,
        transactions,
      });
      budgetMovements = forecastMovements.budgetMovements;
      totalBudgetMovement = forecastMovements.totalBudgetMovement;
    }
    
    // ------------------------
    // 4. Calculate action movements
    // ------------------------
    const actionMovements = [];
    let totalActionMovement = 0;
    
    // Find all actions that could affect this account in this month.
    // Forecast months take their action movements from the shared Analysis
    // forecast math above, so only actual months are scanned here.
    const relevantActions = actions.filter(action => {
      if (isForecastMonth) return false;
      if (!action.isActive) return false;

      // Must affect this account
      const affectsThisAccount = 
        action.sourceAccountId === accountId || 
        action.destinationAccountId === accountId;
      
      if (!affectsThisAccount) return false;
      
      // Check if action occurs in the selected month
      const actionDate = new Date(action.startDate);
      
      // For one-time actions
      if (action.frequency === 'once') {
        return actionDate >= startDate && actionDate <= endDate;
      }
      
      // For recurring actions, check if they're active during this month
      const actionEnd = action.endDate ? new Date(action.endDate) : null;
      const isStartedBeforeMonthEnd = actionDate <= endDate;
      const isActiveInMonth = !actionEnd || actionEnd >= startDate;
      
      // Must have started before month end and still be active in this month
      return isStartedBeforeMonthEnd && isActiveInMonth;
    });
    
    // Process each action to determine how it affects this account
    relevantActions.forEach(action => {
      let amount = action.amount || 0;
      
      // Determine the correct frequency multiplier for the month
      let frequencyMultiplier = 1;
      
      if (action.frequency === 'weekly') {
        // Count actual occurrences of the day in this month
        const [actionMonthName, actionYearStr] = month.split('-');
        const actionMonthIdx = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(actionMonthName);
        const actionYear = 2000 + parseInt(actionYearStr, 10);
        frequencyMultiplier = countWeeklyOccurrencesInMonth(action.startDate, actionYear, actionMonthIdx);
      } else if (action.frequency === 'monthly') {
        frequencyMultiplier = 1; // Once per month
      } else if (action.frequency === 'yearly' && month === action.startDate) {
        frequencyMultiplier = 1; // Once per year, only if this is the month it occurs
      } else if (action.frequency === 'once') {
        frequencyMultiplier = 1; // One-time action
      }
      
      // Apply frequency multiplier for non-one-time actions for forecast months
      if (isForecastMonth && action.frequency !== 'once') {  // Use correct spelling here
        amount = amount * frequencyMultiplier;
      }
      
      // Record action based on type and account involvement
      if (action.type === 'income') {
        // For income actions, money comes into an account
        if (action.destinationAccountId === accountId) {
          // Explicitly set to this account
          actionMovements.push({
            date: new Date(action.startDate).toLocaleDateString(),
            name: action.name || 'Income',
            type: 'income',
            sourceAccount: 'External',
            destAccount: account.name,
            amount: amount,
            frequency: action.frequency
          });
          totalActionMovement += amount;
          // console.log(`Added income action (explicit destination): ${action.name}, amount: +${amount}`);
        } else if (!action.destinationAccountId && accountId === action.sourceAccountId) {
          // Source account is set with no destination - assume it's for this account
          actionMovements.push({
            date: new Date(action.startDate).toLocaleDateString(),
            name: action.name || 'Income',
            type: 'income',
            sourceAccount: 'External',
            destAccount: account.name,
            amount: amount,
            frequency: action.frequency
          });
          totalActionMovement += amount;
          // console.log(`Added income action (source as destination): ${action.name}, amount: +${amount}`);
        } else if (!action.destinationAccountId && !action.sourceAccountId && account.isPrimary) {
          // No accounts specified, use primary account
          actionMovements.push({
            date: new Date(action.startDate).toLocaleDateString(),
            name: action.name || 'Income',
            type: 'income',
            sourceAccount: 'External',
            destAccount: account.name,
            amount: amount,
            frequency: action.frequency
          });
          totalActionMovement += amount;
          // console.log(`Added income action (primary account): ${action.name}, amount: +${amount}`);
        }
      } else if (action.type === 'expense') {
        // For expense actions, money leaves an account
        if (action.sourceAccountId === accountId) {
          // Explicitly set to this account
          actionMovements.push({
            date: new Date(action.startDate).toLocaleDateString(),
            name: action.name || 'Expense',
            type: 'expense',
            sourceAccount: account.name,
            destAccount: 'External',
            amount: -amount,
            frequency: action.frequency
          });
          totalActionMovement -= amount;
          // console.log(`Added expense action (explicit source): ${action.name}, amount: -${amount}`);
        } else if (!action.sourceAccountId && action.destinationAccountId === accountId) {
          // Destination account is set with no source - assume it's for this account
          actionMovements.push({
            date: new Date(action.startDate).toLocaleDateString(),
            name: action.name || 'Expense',
            type: 'expense',
            sourceAccount: account.name,
            destAccount: 'External',
            amount: -amount,
            frequency: action.frequency
          });
          totalActionMovement -= amount;
          // console.log(`Added expense action (destination as source): ${action.name}, amount: -${amount}`);
        } else if (!action.sourceAccountId && !action.destinationAccountId && account.isPrimary) {
          // No accounts specified, use primary account
          actionMovements.push({
            date: new Date(action.startDate).toLocaleDateString(),
            name: action.name || 'Expense',
            type: 'expense',
            sourceAccount: account.name,
            destAccount: 'External',
            amount: -amount,
            frequency: action.frequency
          });
          totalActionMovement -= amount;
          // console.log(`Added expense action (primary account): ${action.name}, amount: -${amount}`);
        }
      } else if (action.type === 'transfer') {
        const sourceAccount = accounts.find(a => a.id === action.sourceAccountId);
        const destAccount = accounts.find(a => a.id === action.destinationAccountId);
        
        if (action.sourceAccountId === accountId) {
          // Money leaving this account (negative)
          actionMovements.push({
            date: new Date(action.startDate).toLocaleDateString(),
            name: action.name || 'Transfer Out',
            type: 'transfer',
            sourceAccount: account.name,
            destAccount: destAccount ? destAccount.name : 'Unknown Account',
            amount: -amount,
            frequency: action.frequency
          });
          totalActionMovement -= amount;
          // console.log(`Added transfer out action: ${action.name}, amount: -${amount}`);
        }
        
        if (action.destinationAccountId === accountId) {
          // Money coming into this account (positive)
          actionMovements.push({
            date: new Date(action.startDate).toLocaleDateString(),
            name: action.name || 'Transfer In',
            type: 'transfer',
            sourceAccount: sourceAccount ? sourceAccount.name : 'Unknown Account',
            destAccount: account.name,
            amount: amount,
            frequency: action.frequency
          });
          totalActionMovement += amount;
          // console.log(`Added transfer in action: ${action.name}, amount: +${amount}`);
        }
      }
    });

    if (forecastMovements) {
      actionMovements.push(...forecastMovements.actionMovements);
      totalActionMovement += forecastMovements.totalActionMovement;
    }

    // ------------------------
    // 5. Calculate ending balance and adjustments
    // ------------------------
    let endingBalance;
    let adjustments = 0;
    
    if (isForecastMonth) {  // Use correct spelling here
      // For forecast months, use the calculated movements
      endingBalance = startingBalance + totalBudgetMovement + totalActionMovement;
      // console.log(`Final forecast calculation: ${startingBalance} + ${totalBudgetMovement} + ${totalActionMovement} = ${endingBalance}`);
    } else {
      // For actual months, use the last transaction's balance if available
      const lastTransactions = transactions
        .filter(t => 
          t.account === accountId && 
          new Date(t.date) >= startDate &&
          new Date(t.date) <= endDate
        )
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      
      if (lastTransactions.length > 0) {
        // Use the last transaction's balance
        endingBalance = lastTransactions[0].balance || 0;
        
        // Calculate adjustments needed to reconcile
        // adjustment = ending_balance - (starting_balance + transaction_movement)
        const calculatedBalance = startingBalance + totalTransactionMovement;
        adjustments = endingBalance - calculatedBalance;
        
        // console.log(`Actual month calculation: Ending balance (${endingBalance}) - (Starting (${startingBalance}) + Transactions (${totalTransactionMovement})) = Adjustment (${adjustments})`);
      } else {
        // If no transactions this month, ending balance equals starting balance
        endingBalance = startingBalance;
        // console.log(`No transactions this month, ending balance equals starting balance: ${endingBalance}`);
      }
    }
    
    return {
      startingBalance,
      transactionMovements: isForecastMonth ? [] : transactionMovements,  // Use correct spelling here
      budgetMovements: isForecastMonth ? budgetMovements : [],  // Use correct spelling here
      actionMovements,
      totalTransactionMovement: isForecastMonth ? 0 : totalTransactionMovement,  // Use correct spelling here
      totalBudgetMovement,
      totalActionMovement,
      adjustments: isForecastMonth ? 0 : adjustments,  // Use correct spelling here
      endingBalance
    };
  }, [accounts, transactions, budgets, actions, categories, allAccountBalancesByMonth, monthlyForecastBalances, forecastMonths, budgetData.months, forecastData, forecastMonths, showForecast, projectedAccountDeltas]);

  // Net worth as at the end of a given month = sum of each account's projected
  // month-end balance. Uses calculateAccountBalanceDetails so it follows the
  // forecast for future months (not just a flat current-balance placeholder).
  const netWorthForMonth = useCallback((month) => {
    if (!month) return 0;
    return accounts.reduce((sum, a) => sum + (calculateAccountBalanceDetails(a.id, month)?.endingBalance || 0), 0);
  }, [accounts, calculateAccountBalanceDetails]);

  const selectedMonthNetWorth = useMemo(
    () => netWorthForMonth(selectedMonth),
    [netWorthForMonth, selectedMonth]
  );

  const selectedMonthNetWorthTrend = useMemo(() => {
    const prev = adjacentMonths.prev;
    if (!prev) return null;
    return getMetricTrend(selectedMonthNetWorth, netWorthForMonth(prev), true, `vs ${prev}`);
  }, [selectedMonthNetWorth, netWorthForMonth, adjacentMonths]);

  // Month-end net worth series for the Net Worth chart. Mirrors the table's
  // Net Worth row (BudgetTableView's getNetWorthFor) so the chart and the
  // table always agree; per-account balances feed the stacked view.
  const netWorthSeries = useMemo(() => displayMonths.map(month => {
    const forecast = forecastMonths.includes(month);
    const useForecastBalances = forecast && monthlyForecastBalances && monthlyForecastBalances[month];
    const balanceFor = (account) => {
      if (useForecastBalances) {
        const details = calculateAccountBalanceDetails(account.id, month);
        return details.endingBalance !== undefined ? details.endingBalance : (account.balance ?? 0);
      }
      return allAccountBalancesByMonth[month]?.[account.id]?.balance ?? 0;
    };
    const accountBalances = {};
    let netWorth = 0;
    accounts.forEach(account => {
      const balance = balanceFor(account);
      accountBalances[account.id] = balance;
      netWorth += balance;
    });
    return { month, netWorth, isForecast: forecast, accounts: accountBalances };
  }), [displayMonths, forecastMonths, monthlyForecastBalances, accounts, allAccountBalancesByMonth, calculateAccountBalanceDetails]);

  useEffect(() => {
    if (selectedMonth) {
      // const balances = calculateAccountBalances(selectedMonth);
      setAccountBalances(selectedMonthBalances);
    }
  }, [selectedMonth, selectedMonthBalances]); 

  const budgetTotals = calculateBudgetTotal();
  
  // Add this effect to calculate and update forecast balances sequentially
  useEffect(() => {
    if (!showForecast || forecastMonths.length === 0) return;
    
    // console.log("Calculating sequential forecasts for months:", forecastMonths);
    
    // Start with current account balances or last actual month balances
    const newMonthlyBalances = { ...monthlyForecastBalances };
    
    // Get the last actual month
    const lastActualMonth = budgetData.months[budgetData.months.length - 1];
    
    // Create a sequential calculation function that processes each month in order
    const calculateSequentialForecasts = () => {
      // Process each forecast month in sequence
      forecastMonths.forEach((month, idx) => {
        const monthBalance = {};
        
        // For each active account, calculate the detailed balance for this month
        accounts.filter(a => a.isActive).forEach(account => {
          // If this is the first forecast month, ensure we're using the last actual month's balance
          if (idx === 0) {
            // Try to get the balance from the last actual month
            const lastActualBalance = 
              allAccountBalancesByMonth[lastActualMonth]?.[account.id]?.balance || 
              account.balance || 0;
              
            // Store this as a temporary value to ensure the first forecast month uses it
            if (!newMonthlyBalances[lastActualMonth]) {
              newMonthlyBalances[lastActualMonth] = {};
            }
            newMonthlyBalances[lastActualMonth][account.id] = lastActualBalance;
          }
          
          // Calculate detailed balance for this account and month
          // This will use the previous month's balance from newMonthlyBalances
          const balanceDetails = calculateAccountBalanceDetails(account.id, month);
          
          // Store the ending balance
          monthBalance[account.id] = balanceDetails.endingBalance;
        });
        
        // Update the monthly balances object with this month's results
        newMonthlyBalances[month] = monthBalance;
      });
      
      return newMonthlyBalances;
    };
    
    // Run the calculation and update state
    const updatedBalances = calculateSequentialForecasts();

    // Only update state if there's a meaningful difference
    if (JSON.stringify(updatedBalances) !== JSON.stringify(monthlyForecastBalances)) {
      setMonthlyForecastBalances(updatedBalances);
    }    
    
  }, [showForecast, forecastMonths, accounts, transactions, budgets, actions, allAccountBalancesByMonth, budgetData.months, monthlyForecastBalances, calculateAccountBalanceDetails]);

  // Create a ref for the dialog wrapper
  const accountBalanceDialogRef = useRef();

  // Create a ref for the dialog wrapper
  const categoryDialogRef = useRef();

  // --- Month Range State for Sankey ---
  // Get all months from budgetData
  const allMonths = useMemo(() => budgetData.months || [], [budgetData.months]);
  const parseMonth = m => {
    if (!m || typeof m !== 'string') return null;
    try {
      const parsed = parse(m, 'MMM-yy', new Date());
      return isValid(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };
  const formatMonth = d => (d && isValid(d) ? format(d, 'MMM-yy') : '');

  // Find the current month string in the data's format (using NZ timezone)
  const getCurrentMonthString = () => {
    return getCurrentNZMonthString();
  };

  // --- Date state for the two charts ---
  const [moneyFlowStart, setMoneyFlowStart] = useState(null);
  const [moneyFlowEnd, setMoneyFlowEnd] = useState(null);
  const [trendsStart, setTrendsStart] = useState(null);
  const [trendsEnd, setTrendsEnd] = useState(null);
  const [trendsChartMode, setTrendsChartMode] = useState('area'); // 'area' | 'stacked'

  // Effect to set initial date range for charts when data is available
  useEffect(() => {
    if (allMonths.length > 0 && !moneyFlowStart && !moneyFlowEnd) {
      const currentMonthStr = getCurrentMonthString();
      const hasCurrentMonth = allMonths.includes(currentMonthStr);
      let initialMonth;
      if (hasCurrentMonth) {
        initialMonth = parseMonth(currentMonthStr);
      } else {
        const last = allMonths[allMonths.length - 1];
        initialMonth = last ? parseMonth(last) : null;
      }
      
      if (initialMonth) {
        setMoneyFlowStart(initialMonth);
        setMoneyFlowEnd(initialMonth);
        
        // For trends, let's set a 12-month range ending at the initial month
        const twelveMonthsAgo = new Date(initialMonth);
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 11);
        

        
        const earliestAvailable = parseMonth(allMonths[0]);
        
        setTrendsStart(twelveMonthsAgo > earliestAvailable ? twelveMonthsAgo : earliestAvailable);
        setTrendsEnd(initialMonth);
      }
    }
  }, [allMonths, moneyFlowStart, moneyFlowEnd]);

  // --- Aggregation for Sankey Month Range ---
  // Helper to get all months in the selected range
  const getMonthsInRange = (start, end) => {
    if (!start || !end) return [];
    const startIdx = allMonths.findIndex(m => {
        const p = parseMonth(m);
        return p && p.getTime() === start.getTime();
    });
    const endIdx = allMonths.findIndex(m => {
        const p = parseMonth(m);
        return p && p.getTime() === end.getTime();
    });
    if (startIdx === -1 || endIdx === -1) return [];
    return allMonths.slice(Math.min(startIdx, endIdx), Math.max(startIdx, endIdx) + 1);
  };

  // Aggregate budget data for the selected range
  const aggregatedBudgetData = useMemo(() => {
    const monthsInRange = getMonthsInRange(moneyFlowStart, moneyFlowEnd);
    if (!monthsInRange.length) return null;
    // Aggregate incomeCategories
    const incomeCategories = {};
    Object.entries(budgetData.incomeCategories).forEach(([cat, monthVals]) => {
      let sum = 0;
      monthsInRange.forEach(m => { sum += monthVals[m] || 0; });
      if (sum !== 0) incomeCategories[cat] = sum;
    });
    // Aggregate expenseCategories
    const expenseCategories = {};
    Object.entries(budgetData.expenseCategories).forEach(([cat, monthVals]) => {
      let sum = 0;
      monthsInRange.forEach(m => { sum += monthVals[m] || 0; });
      if (sum !== 0) expenseCategories[cat] = sum;
    });
    // Aggregate totals
    let totalIncome = 0, totalExpenses = 0;
    monthsInRange.forEach(m => {
      totalIncome += budgetData.monthlySummary[m]?.income || 0;
      totalExpenses += budgetData.monthlySummary[m]?.expenses || 0;
    });
    return {
      monthsInRange,
      incomeCategories,
      expenseCategories,
      totalIncome,
      totalExpenses
    };
  }, [budgetData, moneyFlowStart, moneyFlowEnd, allMonths]);

  // Prepare Sankey data for the range
  const sankeyDataForRange = useMemo(() => {
    if (!aggregatedBudgetData) return null;
    // We'll create a fake single-month summary object for prepareSankeyData
    const fakeBudgetData = {
      incomeCategories: {},
      expenseCategories: {},
      monthlySummary: {
        RANGE: {
          income: aggregatedBudgetData.totalIncome,
          expenses: aggregatedBudgetData.totalExpenses
        }
      }
    };
    // Convert to the format expected by prepareSankeyData
    Object.entries(aggregatedBudgetData.incomeCategories).forEach(([cat, val]) => {
      fakeBudgetData.incomeCategories[cat] = { RANGE: val };
    });
    Object.entries(aggregatedBudgetData.expenseCategories).forEach(([cat, val]) => {
      fakeBudgetData.expenseCategories[cat] = { RANGE: val };
    });
    return prepareSankeyData('RANGE', fakeBudgetData, accounts, categories);
  }, [aggregatedBudgetData, accounts, categories]);

  const getDateRangeString = useMemo(() => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };

    if (viewType === 'monthly' && selectedMonth) {
      const [monthAbbr, yearStr] = selectedMonth.split('-');
      const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthAbbr);
      const year = 2000 + parseInt(yearStr, 10);
      const startDate = new Date(year, monthIndex, 1);
      const endDate = new Date(year, monthIndex + 1, 0);
      return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
    }

    if (viewType === 'yearly') {
      const startDate = new Date(selectedYear, 0, 1);
      const endDate = new Date(selectedYear, 11, 31);
      return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
    }
    
    return '';
  }, [viewType, selectedMonth, selectedYear]);

  const handleDisplayOptionsChange = (event, newValues) => {
    const newShowForecast = newValues.includes('forecast');
    setShowForecast(newShowForecast);
    const newDisplayOptions = newValues.filter(v => v !== 'forecast');
    setDisplayOptions(newDisplayOptions);
  };

  // Toggle a single display option (used by switch-style toggles in yearly view)
  const toggleDisplayOption = (key, checked) => {
    if (key === 'forecast') {
      setShowForecast(checked);
    } else {
      setDisplayOptions(prev => {
        if (checked) return Array.from(new Set([...prev, key]));
        return prev.filter(v => v !== key);
      });
    }
  };

  // Effect to sync Sankey date pickers with the selectedMonth in monthly view
  useEffect(() => {
    if (viewType === 'monthly' && selectedMonth) {
      const parsedMonth = parseMonth(selectedMonth);
      if (parsedMonth) {
        // Money flow is just the selected month
        setMoneyFlowStart(parsedMonth);
        setMoneyFlowEnd(parsedMonth);

        // Trends are the last 12 months ending in selected month
        const twelveMonthsAgo = new Date(parsedMonth);
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 11);
        
        // Find the actual earliest month available in data to prevent gaps
        const earliestAvailable = parseMonth(allMonths[0]);
        
        setTrendsStart(twelveMonthsAgo > earliestAvailable ? twelveMonthsAgo : earliestAvailable);
        setTrendsEnd(parsedMonth);
      }
    }
  }, [viewType, selectedMonth, allMonths]);

  const navButtonSx = {
    color: 'text.secondary',
    p: 0.5,
    '&:hover': { bgcolor: 'grey.100' },
    '&.Mui-disabled': { color: 'grey.300' }
  };

  // Slim month/year stepper row below the header (no card chrome)
  const periodNav = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: viewType === 'yearly' ? 'flex-start' : 'center', gap: 1, mb: 2, px: 0.5 }}>
      {viewType === 'monthly' ? (
        <>
          <IconButton onClick={handlePrevMonth} disabled={!adjacentMonths.prev} size="small" aria-label="Previous month" sx={navButtonSx}>
            <KeyboardArrowLeft fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary', minWidth: 110, textAlign: 'center', whiteSpace: 'nowrap' }}>
            {formatMonthName(selectedMonth)}
          </Typography>
          <IconButton onClick={handleNextMonth} disabled={!adjacentMonths.next} size="small" aria-label="Next month" sx={navButtonSx}>
            <KeyboardArrowRight fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <IconButton onClick={handlePrevYear} disabled={availableYears.indexOf(selectedYear) === 0} size="small" aria-label="Previous year" sx={navButtonSx}>
            <KeyboardArrowLeft fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary', minWidth: 56, textAlign: 'center' }}>
            {selectedYear}
          </Typography>
          <IconButton onClick={handleNextYear} disabled={availableYears.indexOf(selectedYear) === availableYears.length - 1} size="small" aria-label="Next year" sx={navButtonSx}>
            <KeyboardArrowRight fontSize="small" />
          </IconButton>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ pb: 0 }}>
      <PageHeader
        title={viewType === 'monthly' ? 'Cashflow' : 'Net Worth'}
        icon={viewType === 'monthly' ? <InsightsIcon fontSize="large" /> : <AccountBalanceWallet fontSize="large" />}
      />
      {periodNav}

      {/* Display toggles (yearly view) — sit directly on the page background,
          like the Calendar page's options row (no card chrome) */}
      {viewType === 'yearly' && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flexWrap: 'nowrap',
            mb: 2,
            px: 0.5
          }}>
            {/* Display toggles — labelled gray-track pill toggles (multi-select),
                matching the History / Partial months controls on Categories & Accounts */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.25, rowGap: 1 }}>
              <Typography variant="body3" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Options
              </Typography>
              <Box sx={{
                bgcolor: 'rgba(0, 0, 0, 0.05)',
                borderRadius: 6,
                p: 0.5,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.25,
                maxWidth: '100%'
              }}>
                {[{ key: 'percentages', label: 'Prop %' }, { key: 'vs-budget', label: 'vs Bud' }, { key: 'vs-prior-month', label: 'MoM' }, { key: '3m-avg', label: '3M Avg' }, { key: 'forecast', label: 'Forecast', forecast: true }].map(opt => {
                  const checked = opt.forecast ? showForecast : displayOptions.includes(opt.key);
                  return (
                    <ButtonBase
                      key={opt.key}
                      onClick={() => toggleDisplayOption(opt.key, !checked)}
                      aria-pressed={checked}
                      sx={{
                        py: 0.75,
                        px: 1.25,
                        borderRadius: 5,
                        fontFamily: 'inherit',
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        lineHeight: 1.25,
                        whiteSpace: 'nowrap',
                        color: checked ? 'primary.main' : 'text.secondary',
                        bgcolor: checked ? 'white' : 'transparent',
                        transition: 'background-color 0.15s, color 0.15s'
                      }}
                    >
                      {opt.label}
                    </ButtonBase>
                  );
                })}
              </Box>
            </Box>
            {showForecast && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.25 }}>
                <Box sx={{
                  width: 18,
                  height: 12,
                  borderRadius: 0.5,
                  backgroundColor: 'rgba(156, 39, 176, 0.1)',
                  border: '1px dashed rgba(156, 39, 176, 0.5)'
                }} />
                <Typography variant="caption" color="text.secondary">
                  Shaded italic columns are forecast — including the second column for
                  the current month, which is where it lands
                </Typography>
              </Box>
            )}
          </Box>
      )}

      {/* Summary Cards - now outside the header */}
      {viewType === 'monthly' && (
        <Box sx={{
          display: 'grid',
          // Match the Overview metric grid: 2-up on smaller screens, 4-up on
          // wide ones (instead of auto-fit, which packed 3 then wrapped the 4th).
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: { xs: 2, md: 3 },
          mb: 3
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <MetricCard
              label="Net Worth"
              value={formatCurrency(selectedMonthNetWorth)}
              icon={<AccountBalanceWallet />}
              color="primary"
              valueColor="text.primary"
              trend={selectedMonthNetWorthTrend}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <MetricCard
              label="Income"
              value={formatCurrency(selectedMonthSummary.income)}
              icon={<ArrowUpwardIcon />}
              color="success"
              trend={selectedMonthCardTrends.income}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <MetricCard
              label="Expenses"
              value={formatCurrency(selectedMonthSummary.expenses)}
              icon={<ArrowDownwardIcon />}
              color="error"
              trend={selectedMonthCardTrends.expenses}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <MetricCard
              label="Net"
              value={formatCurrency(selectedMonthSummary.balance)}
              icon={<AccountBalanceIcon />}
              color={selectedMonthSummary.balance >= 0 ? 'primary' : 'warning'}
              valueColor={selectedMonthSummary.balance >= 0 ? 'primary.main' : 'error.main'}
              trend={selectedMonthCardTrends.balance}
            />
          </Box>
        </Box>
      )}

      {/* Monthly View */}
      {viewType === 'monthly' && (
        <Box>
          {/* Budget Progress Card stacked above charts */}
          <Box sx={{ mt: 2 }}>
            <BudgetProgressCard selectedMonth={selectedMonth} dateRange={getDateRangeForSelectedMonth} monthSummary={selectedMonthSummary} />
          </Box>
          {/* Charts and Top Categories restored below */}
          {/* Money Flow Diagram + Top Category Trends side by side */}
          {incomeExpenseChartData && incomeExpenseChartData.length > 0 && selectedMonth && (
      <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', xl: 'row' },
                gap: 3,
                mt: 4,
                mb: 3,
        alignItems: 'stretch',
        // Enforce equal heights for both chart cards
        '& > div': { minHeight: { xs: 620, md: 580 } }
              }}
            >
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row' }}>
                <MoneyFlowSection 
                  data={sankeyDataForRange} 
                  formatCurrency={formatCurrency} 
                  categories={categories}
                  startMonth={moneyFlowStart}
                  endMonth={moneyFlowEnd}
                  setStartMonth={setMoneyFlowStart}
                  setEndMonth={setMoneyFlowEnd}
                />
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary', '& svg': { fontSize: '1.1rem' } }}>
                        <ShowChartIcon />
                      </Box>
                      Trends
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      value={trendsChartMode}
                      exclusive
                      onChange={(_, val) => { if (val) setTrendsChartMode(val); }}
                      aria-label="Trends chart mode"
                      sx={{ mr: 0.5, '& .MuiToggleButton-root': { px: 0.5, py: 0.25, minHeight: 28 } }}
                    >
                      <ToggleButton value="area" aria-label="Area / line mode">
                        <ShowChartIcon sx={{ fontSize: 16 }} />
                      </ToggleButton>
                      <ToggleButton value="stacked" aria-label="Stacked bar mode">
                        <StackedBarChartIcon sx={{ fontSize: 16 }} />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Divider />
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <IncomeExpenseChart 
                      data={incomeExpenseChartDataForMonthView} 
                      formatCurrency={formatCurrency}
                      incomeCategoryKeys={Object.keys(budgetData.incomeCategories)}
                      expenseCategoryKeys={Object.keys(budgetData.expenseCategories)}
                      incomeByMonth={budgetData.incomeCategories}
                      expenseByMonth={budgetData.expenseCategories}
                      categories={categories}
                      startMonth={trendsStart}
                      endMonth={trendsEnd}
                      onStartMonthChange={setTrendsStart}
                      onEndMonthChange={setTrendsEnd}
                      chartMode={trendsChartMode}
                      onChartModeChange={setTrendsChartMode}
                      height={380}
                    />
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
          {/* Top Categories Section */}
          {selectedMonth && (
            <Box sx={{ mt: 4 }}>
            </Box>
          )}
        </Box>
      )}

      {/* Table View for Last 12 Months / Calendar Year */}
      {viewType !== 'monthly' && (
        <Box>
          <NetWorthSection
            data={netWorthSeries}
            accounts={accounts}
            formatCurrency={formatCurrency}
          />
          <BudgetTableView
            sx={{padding: 0}}
            displayMonths={displayMonths}
            accounts={accounts}
            budgetData={budgetData}
            forecastData={forecastData}
            forecastMonths={forecastMonths}
            showForecast={showForecast}
            isForecastMonth={isForecastMonth}
            displayOptions={displayOptions}
            getBudgetTarget={getBudgetTarget}
            getVarianceChipProps={getVarianceChipProps}
            getMomChipProps={getMomChipProps}
            formatCurrency={formatCurrency}
            formatPercentage={formatPercentage}
            getCategoryColor={getCategoryColor}
            allAccountBalancesByMonth={allAccountBalancesByMonth}
            monthlyForecastBalances={monthlyForecastBalances}
            handleAccountBalanceClick={handleAccountBalanceClick}
            handleCategoryClick={handleCategoryClick}
            sortedIncomeCategories={sortedIncomeCategories}
            sortedExpenseCategories={sortedExpenseCategories}
            categories={categories}
            calculateAccountBalanceDetails={calculateAccountBalanceDetails}
            budgetTotals={budgetTotals}
            theme={theme}
            compact
          />
        </Box>
      )}

      {/* Replace the old dialog with the wrapper */}
      <CategoryTransactionsDialogWrapper
        ref={categoryDialogRef}
        categories={categories}
        transactions={transactions}
        getDateRangeForSelectedMonth={getDateRangeForSelectedMonth}
        formatMonthName={formatMonthName}
      />

      {/* Replace the old dialog with the wrapper */}
      <AccountBalanceDetailDialogWrapper
        ref={accountBalanceDialogRef}
        accounts={accounts}
        transactions={transactions}
        calculateAccountBalanceDetails={calculateAccountBalanceDetails}
        formatCurrency={formatCurrency}
        isForecastMonth={isForecastMonth}
      />

    </Box>
  );
};

export default BudgetView;
